import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useCoreT } from '../context/CoreLangContext';
import { customerApi } from '../api/apiClient';
import PhoneOtpForm, { buildOtpLabels } from './PhoneOtpForm';
import { useEmailAuthEnabled } from '../hooks/useEmailAuthEnabled';

export default function CustomerAuthModal({ onClose }) {
  const { registerCustomer, loginCustomer, sendCustomerOtp, verifyCustomerOtp, saveCustomerProfile } = useAuth();
  const { tu, restaurantLang } = useLanguage();
  const T = useCoreT();
  const emailAuth = useEmailAuthEnabled() === true; // email option only when explicitly enabled
  // Phone is the priority method, shown first.
  const [method,  setMethod]  = useState('phone'); // 'phone' | 'email'
  const [mode,    setMode]    = useState('login');
  const [resetting, setResetting] = useState(false);  // forgot-password flow
  const [resetPass, setResetPass] = useState('');
  const [form,    setForm]    = useState({ name: '', email: '', password: '' });
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    setError('');
    if (!form.email || !form.password) { setError('Email and password are required.'); return; }
    if (mode === 'register' && !form.name.trim()) { setError('Name is required.'); return; }
    if (mode === 'register' && form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    const res = mode === 'login'
      ? await loginCustomer(form.email, form.password)
      : await registerCustomer({ name: form.name.trim(), email: form.email, password: form.password });
    setLoading(false);
    if (res.success) onClose();
    else setError(res.error);
  };

  return (
    <div className="mc-backdrop" onClick={onClose}>
      <div className="mc-box" onClick={e => e.stopPropagation()}>
        <button className="mc-close" onClick={onClose}>✕</button>

        {/* Method switch: Phone (priority) vs Email. Email only when enabled. */}
        {emailAuth && (
          <div className="mc-method">
            <button className={`mc-method-btn ${method==='phone'?'active':''}`} onClick={() => { setMethod('phone'); setError(''); }}>📱 Phone</button>
            <button className={`mc-method-btn ${method==='email'?'active':''}`} onClick={() => { setMethod('email'); setError(''); }}>✉️ Email</button>
          </div>
        )}

        {(method === 'phone' || !emailAuth) ? (
          <>
            <p className="mc-phone-sub">Sign in or sign up instantly with your mobile number.</p>
            <PhoneOtpForm
              askName
              lang={restaurantLang}
              labels={buildOtpLabels(tu)}
              onSend={sendCustomerOtp}
              onVerify={async (phone, code, name) => {
                const res = await verifyCustomerOtp(phone, code, name);
                if (res.success) onClose();
                return res;
              }}
            />
          </>
        ) : resetting ? (
          <>
            <div className="mc-tabs"><button className="mc-tab active">{T.resetPassword || 'Reset Password'}</button></div>
            <p className="mc-phone-sub">{T.resetPasswordSub || 'Enter your account phone number and a new password. We’ll send a code to confirm.'}</p>
            <div className="mc-field" style={{ marginBottom: '1rem' }}>
              <label>{tu('auth_password')} <span style={{color:'rgba(255,255,255,.3)',fontWeight:400}}>(min 6 chars)</span></label>
              <input className="mc-input" type="password" value={resetPass} onChange={e => setResetPass(e.target.value)} placeholder="••••••••" />
            </div>
            <PhoneOtpForm
              lang={restaurantLang}
              labels={{ ...buildOtpLabels(tu), verifyBtn: T.resetPassword || 'Reset Password' }}
              onSend={async (phone) => {
                if (!resetPass || resetPass.length < 6) return { success: false, error: 'Enter a new password (min 6 chars) first.' };
                try { const r = await customerApi.pwResetSend(phone); return { success: true, ...r }; }
                catch (e) { return { success: false, error: e.message }; }
              }}
              onVerify={async (phone, code) => {
                try {
                  const r = await customerApi.pwResetVerify(phone, code, resetPass);
                  if (r.token) saveCustomerProfile(r.data);  // logged in after reset
                  onClose();
                  return { success: true };
                } catch (e) { return { success: false, error: e.message }; }
              }}
            />
            <button className="mc-link-btn" onClick={() => { setResetting(false); setError(''); }}>← {tu('auth_signin')}</button>
          </>
        ) : (
        <>
        <div className="mc-tabs">
          <button className={`mc-tab ${mode==='login'    ? 'active':''}`} onClick={() => { setMode('login');    setError(''); }}>{tu('auth_signin')}</button>
          <button className={`mc-tab ${mode==='register' ? 'active':''}`} onClick={() => { setMode('register'); setError(''); }}>{tu('auth_register')}</button>
        </div>
        {error && <div className="mc-error">{error}</div>}
        <div className="mc-fields">
          {mode === 'register' && (
            <div className="mc-field">
              <label>{tu('auth_name')}</label>
              <input className="mc-input" value={form.name} onChange={set('name')} placeholder="Your name" autoFocus />
            </div>
          )}
          <div className="mc-field">
            <label>{tu('auth_email')}</label>
            <input className="mc-input" type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" onKeyDown={e => e.key==='Enter' && handleSubmit()} />
          </div>
          <div className="mc-field">
            <label>{tu('auth_password')} {mode==='register' && <span style={{color:'rgba(255,255,255,.3)',fontWeight:400}}>(min 6 chars)</span>}</label>
            <input className="mc-input" type="password" value={form.password} onChange={set('password')} placeholder="••••••••" onKeyDown={e => e.key==='Enter' && handleSubmit()} />
          </div>
        </div>
        <button className="mc-submit" onClick={handleSubmit} disabled={loading}>
          {loading ? '…' : mode==='login' ? tu('auth_signin') : tu('auth_create')}
        </button>
        {mode === 'login' && (
          <button className="mc-link-btn" onClick={() => { setResetting(true); setError(''); }}>
            {T.forgotPassword || 'Forgot password?'}
          </button>
        )}
        </>
        )}
        <style>{`
          .mc-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.7);backdrop-filter:blur(10px);z-index:200;display:flex;align-items:center;justify-content:center;padding:1rem}
          .mc-box{background:#1a1a1a;border:1px solid rgba(255,255,255,.12);border-radius:20px;padding:2rem;width:100%;max-width:360px;position:relative;font-family:'DM Sans',sans-serif}
          .mc-close{position:absolute;top:1rem;right:1rem;background:none;border:none;color:rgba(255,255,255,.4);cursor:pointer;font-size:1rem;padding:.25rem .5rem;border-radius:4px;transition:color .2s}
          .mc-close:hover{color:#fff}
          .mc-method{display:flex;gap:.5rem;margin-bottom:1.25rem;margin-top:.25rem}
          .mc-method-btn{flex:1;padding:.6rem;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);border-radius:10px;color:rgba(255,255,255,.55);cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.85rem;font-weight:600;transition:all .2s}
          .mc-method-btn.active{background:#c8a96e;border-color:#c8a96e;color:#fff}
          .mc-phone-sub{font-size:.82rem;color:rgba(255,255,255,.45);margin:0 0 1rem;line-height:1.5}
          .mc-tabs{display:flex;background:rgba(255,255,255,.05);border-radius:10px;padding:.25rem;margin-bottom:1.5rem;border:1px solid rgba(255,255,255,.08)}
          .mc-tab{flex:1;padding:.6rem;background:none;border:none;color:rgba(255,255,255,.45);cursor:pointer;border-radius:8px;font-family:'DM Sans',sans-serif;font-size:.9rem;transition:all .2s}
          .mc-tab.active{background:#c8a96e;color:#fff;font-weight:700}
          .mc-error{background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.3);border-radius:8px;padding:.65rem 1rem;color:#fca5a5;font-size:.82rem;margin-bottom:1rem}
          .mc-fields{display:flex;flex-direction:column;gap:1rem;margin-bottom:1.25rem}
          .mc-field{display:flex;flex-direction:column;gap:.4rem}
          .mc-field label{font-size:.72rem;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.07em}
          .mc-input{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:8px;padding:.75rem 1rem;color:#fff;font-family:'DM Sans',sans-serif;font-size:.9rem;outline:none;transition:border-color .2s}
          .mc-input:focus{border-color:#c8a96e}
          .mc-submit{width:100%;padding:.9rem;background:#c8a96e;border:none;border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.95rem;font-weight:700;cursor:pointer;transition:opacity .2s}
          .mc-submit:hover:not(:disabled){opacity:.85}
          .mc-submit:disabled{opacity:.5;cursor:not-allowed}
          .mc-link-btn{display:block;width:100%;margin-top:.75rem;background:none;border:none;color:rgba(255,255,255,.45);font-family:'DM Sans',sans-serif;font-size:.8rem;cursor:pointer;text-align:center}
          .mc-link-btn:hover{color:#c8a96e}
        `}</style>
      </div>
    </div>
  );
}
